import { useState, useEffect } from 'react';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import { theme } from '../../theme';

function numberToWords(amount) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function convert(n) {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  }

  if (!amount || amount === 0) return 'Zero';
  const intPart = Math.floor(amount);
  return 'Rupees ' + convert(intPart) + ' Only';
}

function formatINR(amount) {
  if (!amount && amount !== 0) return '—';
  return Number(amount).toLocaleString('en-IN');
}

function generatePayslipHTML(slip) {
  const emp = slip.employee;
  const earnings = slip.earnings || [];
  const deductions = slip.deductions || [];
  const maxRows = Math.max(earnings.length, deductions.length, 3);

  const earningRows = [...earnings, ...Array(Math.max(0, maxRows - earnings.length)).fill(null)];
  const deductionRows = [...deductions, ...Array(Math.max(0, maxRows - deductions.length)).fill(null)];

  const tableRows = earningRows.map((e, i) => {
    const d = deductionRows[i];
    return `
      <tr>
        <td>${e ? e.name : ''}</td>
        <td style="text-align:right">${e ? formatINR(e.amount) : ''}</td>
        <td style="text-align:right">${e ? formatINR(e.amount * 12) : ''}</td>
        <td>${d ? d.name : ''}</td>
        <td style="text-align:right">${d ? (d.amount > 0 ? formatINR(d.amount) : '-') : ''}</td>
        <td style="text-align:right">${d ? (d.amount > 0 ? formatINR(d.amount * 12) : '-') : ''}</td>
      </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Pay Slip - ${slip.period_label}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #fff; padding: 20px; }
    .page { max-width: 780px; margin: 0 auto; border: 1px solid #999; padding: 16px; }
    .header { display: flex; align-items: center; gap: 16px; margin-bottom: 6px; }
    .logo-box { width: 56px; height: 56px; border: 2px solid #e07b00; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 28px; font-weight: 900; color: #e07b00; flex-shrink: 0; }
    .company-name { font-size: 18px; font-weight: 900; text-transform: uppercase; letter-spacing: 1px; }
    .tagline { font-size: 10px; color: #555; font-style: italic; }
    .address { font-size: 10px; color: #333; text-align: center; margin: 4px 0; }
    .slip-title { background: #222; color: #fff; text-align: center; font-size: 12px; font-weight: bold; padding: 5px; margin: 8px 0; letter-spacing: 1px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #ccc; margin-bottom: 10px; }
    .info-row { display: flex; border-bottom: 1px solid #eee; }
    .info-label { font-weight: bold; min-width: 130px; padding: 4px 8px; font-size: 10px; }
    .info-value { padding: 4px 8px; font-size: 10px; }
    table { width: 100%; border-collapse: collapse; font-size: 10.5px; margin-bottom: 6px; }
    th { background: #f0f0f0; border: 1px solid #ccc; padding: 5px 8px; text-align: left; font-weight: bold; font-size: 10px; }
    th:nth-child(2), th:nth-child(3), th:nth-child(5), th:nth-child(6) { text-align: right; }
    td { border: 1px solid #ddd; padding: 4px 8px; }
    .total-row td { font-weight: bold; background: #f9f9f9; border-top: 2px solid #999; }
    .net-row td { font-weight: bold; font-size: 11px; }
    .inwords { border: 1px solid #ddd; padding: 6px 10px; font-size: 10.5px; margin-bottom: 12px; }
    .inwords strong { font-weight: bold; }
    .signature-row { display: flex; justify-content: space-between; margin-top: 40px; font-size: 10px; }
    .sig-box { text-align: center; }
    .sig-line { border-top: 1px solid #000; width: 160px; margin-bottom: 4px; }
    @media print {
      body { padding: 0; }
      .page { border: none; }
      @page { margin: 1cm; size: A4; }
    }
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    <div class="logo-box">B</div>
    <div>
      <div class="company-name">BonTon Softwares Private Limited</div>
      <div class="tagline">Win In Style</div>
    </div>
  </div>
  <div class="address">No.82, "High Gates" V Floor, Santhome High Road, MRC Nagar, Chennai - 600028</div>
  <div class="slip-title">Pay Slip for the month of ${slip.period_label}</div>

  <div class="info-grid">
    <div>
      <div class="info-row"><span class="info-label">Employee Name:</span><span class="info-value">${emp.full_name}</span></div>
      <div class="info-row"><span class="info-label">Employee ID</span><span class="info-value">${emp.emp_code}</span></div>
      <div class="info-row"><span class="info-label">Employee Address:</span><span class="info-value">${emp.address}</span></div>
      <div class="info-row"><span class="info-label">PAN</span><span class="info-value">${emp.pan_number}</span></div>
    </div>
    <div>
      <div class="info-row"><span class="info-label">Date of Joining</span><span class="info-value">${emp.hire_date ? new Date(emp.hire_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span></div>
      <div class="info-row"><span class="info-label">Designation</span><span class="info-value">${emp.designation}</span></div>
      <div class="info-row"><span class="info-label">Gross Salary</span><span class="info-value">${formatINR(slip.gross_salary)}/-</span></div>
      <div class="info-row"><span class="info-label">PF UAN</span><span class="info-value">${emp.pf_uan}</span></div>
      <div class="info-row"><span class="info-label">ESIC No</span><span class="info-value">${emp.esic_number || '0'}</span></div>
      <div class="info-row"><span class="info-label">Bank Account No:</span><span class="info-value">${emp.bank_account_number}</span></div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:22%">Earnings</th>
        <th style="width:13%">Amount (In Rs)</th>
        <th style="width:13%">YTD (In Rs)</th>
        <th style="width:22%">Deductions</th>
        <th style="width:13%">Amount (In Rs)</th>
        <th style="width:13%">YTD (In Rs)</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows}
      <tr class="total-row">
        <td>Total Earnings</td>
        <td style="text-align:right">${formatINR(slip.gross_salary)}</td>
        <td style="text-align:right">${formatINR(slip.gross_salary * 12)}</td>
        <td>Total Deduction</td>
        <td style="text-align:right">${formatINR(slip.total_deductions)}</td>
        <td style="text-align:right">${formatINR(slip.total_deductions * 12)}</td>
      </tr>
      <tr class="net-row">
        <td><strong>NET Salary</strong></td>
        <td style="text-align:right"><strong>${formatINR(slip.net_salary)}</strong></td>
        <td></td>
        <td><strong>YTD NET Salary</strong></td>
        <td style="text-align:right" colspan="2"><strong>${formatINR(slip.net_salary * 12)}</strong></td>
      </tr>
    </tbody>
  </table>

  <div class="inwords"><strong>In Words:</strong> ${numberToWords(slip.net_salary)}</div>

  <div class="signature-row">
    <div class="sig-box">
      <div class="sig-line"></div>
      <div>Employee Signature</div>
    </div>
    <div class="sig-box">
      <div class="sig-line"></div>
      <div>Employer Signature</div>
    </div>
  </div>
</div>
<script>window.onload = function() { window.print(); }</script>
</body>
</html>`;
}

function MyPayslips() {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrms.getMyPayslips()
      .then((r) => { setPayslips(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleDownload = (slip) => {
    const html = generatePayslipHTML(slip);
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>My Payslips</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
          {payslips.length} payslip{payslips.length !== 1 ? 's' : ''} available
        </p>
      </div>

      {payslips.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
            <p style={{ color: theme.text, fontWeight: 600, marginBottom: '0.5rem' }}>No payslips available yet</p>
            <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>Payslips appear here after payroll is processed by HR</p>
          </div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {payslips.map((slip) => (
            <div key={slip.id} style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              {/* Month Icon */}
              <div style={{ width: '52px', height: '52px', borderRadius: '0.625rem', background: `${theme.primary}15`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '0.65rem', fontWeight: 700, color: theme.primary, textTransform: 'uppercase' }}>{slip.month_name.slice(0, 3)}</span>
                <span style={{ fontSize: '1rem', fontWeight: 800, color: theme.primary }}>{slip.year}</span>
              </div>

              {/* Details */}
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '1rem', color: theme.text }}>{slip.period_label}</p>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.8rem', color: theme.textMuted }}>
                    Gross: <strong style={{ color: theme.text }}>₹{formatINR(slip.gross_salary)}</strong>
                  </span>
                  <span style={{ fontSize: '0.8rem', color: theme.textMuted }}>
                    Deductions: <strong style={{ color: theme.danger }}>₹{formatINR(slip.total_deductions)}</strong>
                  </span>
                  <span style={{ fontSize: '0.8rem', color: theme.textMuted }}>
                    Net Pay: <strong style={{ color: theme.success, fontSize: '0.9rem' }}>₹{formatINR(slip.net_salary)}</strong>
                  </span>
                </div>
              </div>

              {/* Status + Download */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                <Badge status={slip.status} />
                <Button variant="primary" size="sm" onClick={() => handleDownload(slip)}>
                  ⬇ Download PDF
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyPayslips;
